import {
  GDSHeadingLevel,
  GDSIconSource,
  GDSSpace,
} from '@leagueplatform/genesis-core';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { SectionHeader } from '@leagueplatform/genesis-core-alpha';
import { type MasonryDriverAction } from '@leagueplatform/masonry-renderers';
import { MasonryTextAction } from 'components/masonry-section-header/section-header-text-action.component';
import { getSectionHeaderId } from 'utils/get-section-header-id.util';

export const formatClassNames = (...classNames: (string | undefined)[]) =>
  classNames?.filter(Boolean).join(' ') || '';

export interface MasonrySectionHeaderNodeProperties {
  buttonLabel?: string;
  heading: string;
  headingLevel?: GDSHeadingLevel;
  isCtaLabelledByHeading?: boolean;
  paddingTop?: GDSSpace;
  subtitle?: string;
  buttonLabelIcon?: GDSIconSource;
}

export type MasonrySectionHeaderNodeActions = {
  onButtonClicked?: MasonryDriverAction;
};

export type MasonrySectionHeaderNode = MasonryEngineNode<
  'sectionHeader',
  MasonrySectionHeaderNodeProperties,
  MasonrySectionHeaderNodeActions
>;

export type MasonrySectionHeaderRendererProps =
  MasonryEngineNodeRendererProps<MasonrySectionHeaderNode>;

export const SectionHeaderRenderer = ({
  heading,
  headingLevel,
  buttonLabel,
  buttonLabelIcon,
  subtitle,
  onButtonClicked: onClick,
  isCtaLabelledByHeading,
  paddingTop = '$one',
  rootElementRef,
  className,
  themeOverrideClassName,
  locatorId,
  css,
}: MasonrySectionHeaderRendererProps) => {
  const hasTextAction: boolean = !!onClick && !!buttonLabel;
  const headingId = getSectionHeaderId(heading);
  return (
    <SectionHeader
      css={{ marginBlockStart: paddingTop, ...css }}
      headingLevel={headingLevel}
      isCtaLabelledByHeading={isCtaLabelledByHeading ?? undefined}
      subtitle={subtitle}
      textAction={
        hasTextAction ? (
          <MasonryTextAction
            onClick={onClick}
            text={buttonLabel}
            icon={buttonLabelIcon}
          />
        ) : undefined
      }
      title={heading}
      ref={rootElementRef}
      className={formatClassNames(themeOverrideClassName, className)}
      data-testid={locatorId}
      headingId={headingId}
    />
  );
};
